import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Search, Filter, X, MapPin, ArrowLeft, Wrench, Zap, Trees, Sparkles, Fan, Home, 
  Paintbrush, Square, Hammer, Layers, Boxes, PanelTop, Lock, Bug, 
  Refrigerator, Fence, Trash2, Shovel, DoorOpen, Globe, Plus, ExternalLink, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServiceCard from "../components/ServiceCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Wrench, Zap, Trees, Sparkles, Fan, Home, Paintbrush, Square,
  Hammer, Layers, Boxes, PanelTop, Lock, Bug, Refrigerator,
  Fence, Trash2, Shovel, DoorOpen
};

const CategoryIcon = ({ icon, className }) => {
  const IconComponent = iconMap[icon] || Wrench;
  return <IconComponent className={className} />;
};

const Directory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "all");
  const [showFilters, setShowFilters] = useState(false);

  // Web search state
  const [webResults, setWebResults] = useState([]);
  const [webSearching, setWebSearching] = useState(false);
  const [webSearchLocation, setWebSearchLocation] = useState("New Braunfels, TX 78130");
  const [showLocationInput, setShowLocationInput] = useState(false);
  const [addingContractor, setAddingContractor] = useState(null);
  const [hasSearchedWeb, setHasSearchedWeb] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch (error) {
        // Not logged in
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setWebResults([]);
      setHasSearchedWeb(false);
      try {
        // Fetch categories
        const catRes = await axios.get(`${API}/categories`);
        setCategories(catRes.data);

        // Build query params
        const params = new URLSearchParams();
        if (searchQuery) params.append("search", searchQuery);
        if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);

        // Fetch services
        const svcRes = await axios.get(`${API}/services?${params.toString()}`);
        setServices(svcRes.data);

        // Auto-trigger web search if no results and there's a search query
        if (svcRes.data.length === 0 && searchQuery) {
          handleWebSearch();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchQuery, selectedCategory]);

  // Web search handler
  const handleWebSearch = async () => {
    setWebSearching(true);
    setHasSearchedWeb(true);
    
    const categoryName = selectedCategory && selectedCategory !== "all"
      ? categories.find(c => c.category_id === selectedCategory)?.name || ""
      : "";
    
    try {
      const res = await axios.post(`${API}/web-search`, {
        query: searchQuery || categoryName || "contractor",
        location: webSearchLocation,
        category: categoryName
      });
      
      setWebResults(res.data.results || []);
      
      // If no results, show helpful message with Google link
      if (res.data.results?.length === 0) {
        const googleUrl = res.data.google_url || `https://www.google.com/search?q=${encodeURIComponent(res.data.suggestion || searchQuery || categoryName)}`;
        toast.info(
          <div className="space-y-2">
            <p className="font-medium">No additional contractors found in our extended search.</p>
            <a 
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#1A4D2E] underline hover:text-[#143D24]"
            >
              Try searching Google →
            </a>
          </div>,
          { duration: 6000 }
        );
      }
    } catch (error) {
      console.error("Web search error:", error);
      // On error, still provide a helpful fallback
      const fallbackQuery = `${searchQuery || ""} ${selectedCategory && selectedCategory !== "all" ? categories.find(c => c.category_id === selectedCategory)?.name || "" : "contractor"} ${webSearchLocation}`;
      toast.info(
        <div className="space-y-2">
          <p>Web search unavailable right now.</p>
          <a 
            href={`https://www.google.com/search?q=${encodeURIComponent(fallbackQuery)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[#1A4D2E] underline"
          >
            Search Google instead →
          </a>
        </div>,
        { duration: 6000 }
      );
    } finally {
      setWebSearching(false);
    }
  };

  // Add contractor from web results (admin only)
  const handleAddContractor = async (result) => {
    if (!user || user.role !== "admin") {
      toast.error("Only admins can add contractors");
      return;
    }

    const categoryId = selectedCategory && selectedCategory !== "all" 
      ? selectedCategory 
      : "cat_handyman"; // Default category

    setAddingContractor(result.name);
    
    try {
      await axios.post(`${API}/add-from-web`, {
        name: result.name,
        category_id: categoryId,
        description: result.description || "",
        phone: result.phone || "",
        email: "",
        address: result.address || "",
        city: webSearchLocation.split(",")[0].trim() || "New Braunfels",
        website: result.website || ""
      }, { withCredentials: true });

      toast.success(`${result.name} added to database!`);
      
      // Remove from web results
      setWebResults(webResults.filter(r => r.name !== result.name));
      
      // Refresh services
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (selectedCategory && selectedCategory !== "all") params.append("category", selectedCategory);
      const svcRes = await axios.get(`${API}/services?${params.toString()}`);
      setServices(svcRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contractor");
    } finally {
      setAddingContractor(null);
    }
  };

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set("search", searchQuery);
    if (selectedCategory && selectedCategory !== "all") params.set("category", selectedCategory);
    setSearchParams(params);
  }, [searchQuery, selectedCategory, setSearchParams]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
  };

  const hasActiveFilters = searchQuery || (selectedCategory && selectedCategory !== "all");

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="directory-page">
      <Header user={user} />

      <main className="pt-24 lg:pt-28 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-[#1A1A1A] mb-2">
              Subcontractor Directory
            </h1>
            <p className="text-[#4A4A4A]">
              Find trusted subcontractors across 20+ trade categories
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 lg:p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  type="text"
                  placeholder="Search services..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-stone-50 border-stone-200 focus:border-[#1A4D2E]"
                  data-testid="search-input"
                />
              </div>

              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full lg:w-48 h-12" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="h-12 text-[#71717A] hover:text-[#1A1A1A]"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Categories Row (when no category selected) */}
          {(!selectedCategory || selectedCategory === "all") && categories.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4">
                Browse Categories
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {categories.map((category) => (
                  <button
                    key={category.category_id}
                    onClick={() => setSelectedCategory(category.category_id)}
                    className="group flex items-start gap-3 p-4 bg-white rounded-xl border border-stone-200 hover:border-[#1A4D2E]/30 hover:shadow-md transition-all duration-300 text-left cursor-pointer"
                    data-testid={`category-btn-${category.slug}`}
                  >
                    <div className="w-10 h-10 bg-[#1A4D2E]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#1A4D2E] transition-colors duration-300">
                      <CategoryIcon icon={category.icon} className="w-5 h-5 text-[#1A4D2E] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[#1A1A1A] group-hover:text-[#1A4D2E] transition-colors text-sm">
                        {category.name}
                      </h3>
                      <p className="text-[#71717A] text-xs line-clamp-2 mt-0.5">{category.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {selectedCategory && selectedCategory !== "all" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedCategory("all")}
                    className="border-[#1A4D2E] text-[#1A4D2E] rounded-full"
                    data-testid="back-to-all-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    All Categories
                  </Button>
                )}
                <h2 className="text-lg font-semibold text-[#1A1A1A]">
                  {selectedCategory && selectedCategory !== "all"
                    ? categories.find((c) => c.category_id === selectedCategory)?.name || "Services"
                    : "All Services"}
                  <span className="text-[#71717A] font-normal ml-2">
                    ({services.length} {services.length === 1 ? "result" : "results"})
                  </span>
                </h2>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-80 bg-white rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : services.length === 0 ? (
              <div className="space-y-8">
                {/* No database results message */}
                <div className="text-center py-12 bg-white rounded-2xl border border-stone-200">
                  <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-[#71717A]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">
                    No services found in our directory
                  </h3>
                  <p className="text-[#4A4A4A] mb-6">
                    {searchQuery ? `No results for "${searchQuery}"` : "Try adjusting your search or filters"}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    {hasActiveFilters && (
                      <Button
                        onClick={clearFilters}
                        variant="outline"
                        className="border-[#1A4D2E] text-[#1A4D2E]"
                      >
                        Clear All Filters
                      </Button>
                    )}
                    
                    {/* Web Search Button */}
                    <Button
                      onClick={handleWebSearch}
                      disabled={webSearching}
                      className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                      data-testid="web-search-btn"
                    >
                      {webSearching ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Searching Web...
                        </>
                      ) : (
                        <>
                          <Globe className="w-4 h-4 mr-2" />
                          Search the Web
                        </>
                      )}
                    </Button>
                    
                    {/* Add Subcontractor Button */}
                    {user && (
                      <Button
                        asChild
                        className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                        data-testid="add-subcontractor-empty-btn"
                      >
                        <a href={`/create-listing${selectedCategory && selectedCategory !== "all" ? `?category=${selectedCategory}` : ""}`}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add a Subcontractor
                        </a>
                      </Button>
                    )}
                  </div>

                  {/* Location selector */}
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <MapPin className="w-4 h-4 text-[#71717A]" />
                    {showLocationInput ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={webSearchLocation}
                          onChange={(e) => setWebSearchLocation(e.target.value)}
                          placeholder="City, State or ZIP"
                          className="w-48 h-8 text-sm"
                          data-testid="location-input"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowLocationInput(false)}
                        >
                          Done
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowLocationInput(true)}
                        className="text-sm text-[#4A4A4A] hover:text-[#1A4D2E] underline"
                      >
                        {webSearchLocation}
                      </button>
                    )}
                  </div>
                </div>

                {/* Web Search Results */}
                {webResults.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Globe className="w-5 h-5 text-[#E07A5F]" />
                      <h3 className="text-lg font-semibold text-[#1A1A1A]">
                        Web Results
                      </h3>
                      <span className="text-sm text-[#71717A]">
                        ({webResults.length} found online)
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {webResults.map((result, index) => (
                        <div
                          key={index}
                          className="bg-white rounded-xl border border-dashed border-[#E07A5F]/50 p-5 hover:border-[#E07A5F] transition-colors"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="font-semibold text-[#1A1A1A] line-clamp-1">
                              {result.name}
                            </h4>
                            <span className="text-xs bg-[#E07A5F]/10 text-[#E07A5F] px-2 py-0.5 rounded-full">
                              Web
                            </span>
                          </div>
                          
                          <p className="text-sm text-[#4A4A4A] mb-3 line-clamp-2">
                            {result.description || "No description available"}
                          </p>
                          
                          {result.phone && (
                            <p className="text-sm text-[#71717A] mb-1">
                              📞 {result.phone}
                            </p>
                          )}
                          
                          {result.address && (
                            <p className="text-sm text-[#71717A] mb-3 line-clamp-1">
                              📍 {result.address}
                            </p>
                          )}
                          
                          <div className="flex gap-2 mt-4">
                            {result.website && (
                              <Button
                                size="sm"
                                variant="outline"
                                asChild
                                className="flex-1"
                              >
                                <a href={result.website} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  Website
                                </a>
                              </Button>
                            )}
                            
                            {user?.role === "admin" && (
                              <Button
                                size="sm"
                                onClick={() => handleAddContractor(result)}
                                disabled={addingContractor === result.name}
                                className="flex-1 bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                                data-testid={`add-contractor-${index}`}
                              >
                                {addingContractor === result.name ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No web results message */}
                {hasSearchedWeb && webResults.length === 0 && !webSearching && (
                  <div className="text-center py-8 bg-stone-50 rounded-xl">
                    <p className="text-[#71717A] mb-3">No web results found either.</p>
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(
                        `${searchQuery || ""} contractor ${webSearchLocation}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#1A4D2E] hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Search Google directly
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Database Results */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service, index) => (
                    <div
                      key={service.service_id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <ServiceCard service={service} />
                    </div>
                  ))}
                </div>

                {/* Search Web for More button */}
                {services.length > 0 && services.length < 10 && (
                  <div className="mt-8 text-center flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Button
                      onClick={handleWebSearch}
                      disabled={webSearching}
                      variant="outline"
                      className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10"
                      data-testid="search-more-btn"
                    >
                      {webSearching ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Globe className="w-4 h-4 mr-2" />
                          Search Web for More Options
                        </>
                      )}
                    </Button>
                    
                    {user && (
                      <Button
                        asChild
                        className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                        data-testid="add-subcontractor-btn"
                      >
                        <a href={`/create-listing${selectedCategory && selectedCategory !== "all" ? `?category=${selectedCategory}` : ""}`}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add a Subcontractor
                        </a>
                      </Button>
                    )}
                  </div>
                )}

                {/* Web Results Section (when there are also database results) */}
                {webResults.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-stone-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Globe className="w-5 h-5 text-[#E07A5F]" />
                      <h3 className="text-lg font-semibold text-[#1A1A1A]">
                        Additional Web Results
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {webResults.map((result, index) => (
                        <div
                          key={index}
                          className="bg-white rounded-xl border border-dashed border-[#E07A5F]/50 p-5"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="font-semibold text-[#1A1A1A] line-clamp-1">
                              {result.name}
                            </h4>
                            <span className="text-xs bg-[#E07A5F]/10 text-[#E07A5F] px-2 py-0.5 rounded-full">
                              Web
                            </span>
                          </div>
                          <p className="text-sm text-[#4A4A4A] mb-3 line-clamp-2">
                            {result.description || "No description available"}
                          </p>
                          <div className="flex gap-2">
                            {result.website && (
                              <Button size="sm" variant="outline" asChild className="flex-1">
                                <a href={result.website} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  Website
                                </a>
                              </Button>
                            )}
                            {user?.role === "admin" && (
                              <Button
                                size="sm"
                                onClick={() => handleAddContractor(result)}
                                disabled={addingContractor === result.name}
                                className="flex-1 bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                              >
                                {addingContractor === result.name ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Directory;
